/**
 * Acompanhamento — página de consulta de processos.
 *
 * Esta refatoração adota o mesmo padrão modular usado no projeto
 * CadastroEmpresa, separando responsabilidades em:
 *
 *   - ./services    → chamadas à API + casos de uso
 *   - ./mappers     → conversão payload ↔ modelo da UI
 *   - ./utils       → identidade, documento, cache, sessão
 *   - ./ui          → resumo de item + helpers de seleção
 *
 * A view (React) consome apenas esses módulos, mantendo a página curta e
 * cada comportamento isolado em arquivos testáveis.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaSearch, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./style.css";
import BotaoBusca from "../../components/BotaoBusca.js";
import Loader from "../../components/Loader/index.js";

import {
  carregarAcompanhamentosDoUsuario,
  buscarStatusCompletoDaEmpresa,
} from "./services/carregarAcompanhamentos.js";
import {
  normalizarStatusCadastro,
  STATUS_PARA_ETAPA,
  STATUS_LABELS,
  STATUS_CORES,
  obterTotalEtapasPorPrograma,
} from "./mappers/statusMapper.js";
import { obterEtapaRejeicaoPorHint } from "./mappers/rejeicaoMapper.js";
import { cadastroRequerSupex } from "./mappers/supexMapper.js";
import { obterResumoStatusItem } from "./ui/itemResumo.js";
import {
  CACHE_KEY_ACOMPANHAMENTO_LISTA,
  obterIdentidadeSessao,
  salvarCacheComContexto,
} from "./utils/session.js";
import { montarDescricaoEmpresa } from "./utils/empresa.js";
import { formatarCnpjCpf } from "./utils/documento.js";
import {
  obterTipoProgramaUsuarioLogado,
  resolverIdentificacaoPrograma,
} from "./utils/tipoPrograma.js";

/* ============================================================
   Constantes locais (passos visuais do stepper)
   - programa 0 (TCE) e 2 (TCA Empregador): 5 etapas, com
     "Aguardando Preenchimento do Aprendiz" entre cadastro inicial e
     validação, e "Contrato Finalizado" como etapa final;
   - programa 1 (TCA) ou CIEE/consultor: 4 etapas (fluxo padrão).
============================================================ */

const etapasBasePorPrograma = {
  // 0 e 2: o estudante preenche DEPOIS do cadastro inicial e ANTES da validação
  0: [
    { id: 1, nome: "Cadastro inicial" },
    { id: 2, nome: "Aguardando Preenchimento do Aprendiz" },
    { id: 3, nome: "Validação do preenchimento" },
    { id: 4, nome: "Aguardando assinatura" },
    { id: 5, nome: "Contrato Finalizado" },
  ],
  2: [
    { id: 1, nome: "Cadastro inicial" },
    { id: 2, nome: "Aguardando Preenchimento do Aprendiz" },
    { id: 3, nome: "Validação do preenchimento" },
    { id: 4, nome: "Aguardando assinatura" },
    { id: 5, nome: "Contrato Finalizado" },
  ],
  // 1 (TCA) e qualquer outro (CIEE/consultor): fluxo padrão sem etapa extra
  1: [
    { id: 1, nome: "Cadastro inicial" },
    { id: 2, nome: "Validação do preenchimento" },
    { id: 3, nome: "Aguardando assinatura" },
    { id: 4, nome: "Contrato Finalizado" },
  ],
};

const obterEtapasExemplo = (tipoPrograma = 1) =>
  etapasBasePorPrograma[Number(tipoPrograma)] || etapasBasePorPrograma[1];

/* ============================================================
   Constantes de configuração (espelho do CadastroEmpresa)
============================================================ */

const POLL_INTERVAL_MS = 15000;
const PAGE_SIZE = 50;
const CACHE_KEY = CACHE_KEY_ACOMPANHAMENTO_LISTA;

// Fallback usado pelo componente `Stepper` quando o pai não envia a
// prop `etapas`. Definido no escopo do módulo (não dentro do componente
// `Acompanhamento`) para que o `Stepper` — também declarado aqui — possa
// usá-lo sem depender de hooks.
const ETAPAS_FALLBACK = etapasBasePorPrograma[1];

const Stepper = ({ etapaAtual = 1, etapas }) => {
  const etapasSeguras =
    Array.isArray(etapas) && etapas.length > 0 ? etapas : ETAPAS_FALLBACK;
  return (
    <div className="stepper-wrapper">
      <div className="stepper-linha-bg" />
      <div
        className="stepper-linha-progresso"
        style={{
          width: `${
            etapasSeguras.length > 1
              ? ((etapaAtual - 1) / (etapasSeguras.length - 1)) * 100
              : 0
          }%`,
        }}
      />
      <div className="stepper-bolinhas">
        {etapasSeguras.map((etapa, index) => {
          const numero = index + 1;
          const status =
            numero < etapaAtual
              ? "concluida"
              : numero === etapaAtual
                ? "andamento"
                : "pendente";

          return (
            <div className="stepper-etapa" key={etapa.id}>
              <div className={`stepper-bolinha stepper-bolinha-${status}`}>
                {status === "concluida" ? "✓" : numero}
              </div>
              <span className={`stepper-label stepper-label-${status}`}>
                {etapa.nome}
              </span>
            </div>
          );
        })}
      </div>
      <p className="stepper-status-texto">
        Etapa {etapaAtual} de {etapasSeguras.length} —{" "}
        <strong>{etapasSeguras[etapaAtual - 1]?.nome}</strong>
      </p>
    </div>
  );
};

const Acompanhamento = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [itens, setItens] = useState([]);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [buscaDigitada, setBuscaDigitada] = useState("");
  const [temMais, setTemMais] = useState(false);
  const [proximaPagina, setProximaPagina] = useState(2);
  const [carregandoMais, setCarregandoMais] = useState(false);

  // Inicialização SÍNCRONA do token: evita o bug "Token obrigatório"
  // quando callbacks (executarBusca, polling, selecionarItem) rodam
  // no primeiro render, antes do useEffect que populava o token.
  const tokenRef = useRef(typeof window !== "undefined"
    ? localStorage.getItem("token")
    : null);
  const pollingRef = useRef(null);
  const pollingEmpresaIdRef = useRef(null);
  const abortRef = useRef(null);
  const itemSelecionadoRef = useRef(null);

  // Limpa a seleção salva no localStorage ao abrir a página
  // para forçar lista vazia + sem stepper selecionado.
  useEffect(() => {
    localStorage.removeItem("empresa_id_acompanhamento");
    localStorage.removeItem("empresa_busca_acompanhamento");
  }, []);

  const busca = buscaDigitada.trim();

  /* --------------------------------------------------------
     Polling periódico (espelha o iniciarPolling do CadastroEmpresa)
     - Guarda por empresaId para evitar criar 2+ intervals
       para o mesmo item (Strict Mode dev + re-renders).
  -------------------------------------------------------- */
  const iniciarPolling = useCallback(
    (empresaRef) => {
      if (!empresaRef) return;
      // Migração para o novo contrato do backend: o id do TCA Empregador
      // vem em `tca_id` (preferência) ou `tcaId`. `item.id` genérico NÃO
      // é confiável porque pode estar apontando para `empresa.id`.
      const novoId = String(
        empresaRef.tca_id ?? empresaRef.tcaId ?? empresaRef.id,
      );
      if (!novoId || novoId === "undefined" || novoId === "null") return;

      // Se já existe polling para esta empresa, não cria outro.
      if (pollingRef.current && pollingEmpresaIdRef.current === novoId) {
        return;
      }

      // Estudante não tem polling no Acompanhamento — clicar no card já
      // navega para o preenchimento daquela autorização específica, e o
      // status detalhado é carregado on-demand no detalhe (TCE / TCA Emp.).
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const decoded = jwtDecode(token);
          const isEstudante =
            decoded?.tipo === "estudante" ||
            Number(decoded?.acesso_autorizacao ?? 0) === 1;
          if (isEstudante) return;
        }
      } catch (_) {
        /* fallback: segue fluxo padrão */
      }

      // Empresa mudou → limpa o anterior e cria um novo.
      if (pollingRef.current) clearInterval(pollingRef.current);

      pollingEmpresaIdRef.current = novoId;
      pollingRef.current = setInterval(async () => {
        try {
          // ⚠️ Wrapper em carregarAcompanhamentos.js espera
          // (empresaRef, token, signal) POSICIONAL — passar
          // {token,id} faz empresaRef virar um objeto, quebrar
          // obterIdTca e cair em "Token obrigatório" no fallback.
          const contexto = await buscarStatusCompletoDaEmpresa(
            empresaRef,
            tokenRef.current,
          );

          setItemSelecionado((atual) => {
            if (!atual) return atual;
            // CORREÇÃO C: NÃO sobrescrever campos de identidade (CNPJ,
            // Razão Social, nome do Aprendiz) com dados do `/status-completo`.
            // Esses campos SEMPRE vêm do item da lista. Só atualizamos
            // os campos DERIVADOS de acompanhamento.
            const payloadCru = contexto.payload || {};

            // ANTI-REGRESSÃO: o /status-completo pode devolver
            // eventualmente uma `etapa_atual` MENOR que a etapa que o
            // frontend já conhece (ex.: o backend recalcula a etapa
            // a partir do último evento de `historico_eventos`, e o
            // último evento pode ser o PENDENTE de criação, voltando
            // o stepper da 2 para 1; ou um evento de ASSINATURA volta
            // um contrato FINALIZADO da 5 para 4). Para evitar
            // "voltas", só aceitamos avançar; nunca regredir.
            const etapaJaVista = Number(
              atual?.etapa_atual ?? atual?.raw?.etapa_atual ?? 0,
            );
            const etapaNova = Number(payloadCru.etapa_atual ?? 0);
            const etapaFinal =
              Number.isFinite(etapaNova) && etapaNova > 0
                ? Math.max(etapaNova, etapaJaVista || 0)
                : etapaJaVista || null;

            const camposDerivados = {
              status: payloadCru.status ?? atual.status,
              etapa_atual: etapaFinal ?? payloadCru.etapa_atual,
              total_etapas: payloadCru.total_etapas,
              label: payloadCru.label,
              cor: payloadCru.cor,
              status_label: payloadCru.status_label,
              rejeitado: payloadCru.rejeitado,
              finalizado: payloadCru.finalizado,
              supex: payloadCru.supex,
              ultimo_evento: payloadCru.ultimo_evento,
              historico: payloadCru.historico,
            };
            // camposDerivados aplicado por último para preservar a proteção anti-regressão (Math.max) em raw.etapa_atual
            const rawAtualizado = payloadCru
              ? { ...(atual?.raw || {}), ...payloadCru, ...camposDerivados }
              : atual?.raw;
            return {
              ...atual,
              ...camposDerivados,
              raw: rawAtualizado,
            };
          });
        } catch (error) {
          // Erro 401 → token inválido/expirado: para o polling e
          // redireciona para o login em vez de martelar a API.
          const status = error?.response?.status;
          if (status === 401 || status === 403) {
            if (pollingRef.current) clearInterval(pollingRef.current);
            pollingRef.current = null;
            pollingEmpresaIdRef.current = null;
            setErro("Sessão expirada. Faça login novamente.");
            navigate("/login", { replace: true });
            return;
          }
          // Ignora cancelamentos (não é erro real).
          if (
            error?.name === "CanceledError" ||
            error?.code === "ERR_CANCELED"
          ) {
            return;
          }
          setErro(`Erro ao atualizar status: ${error.message}`);
        }
      }, POLL_INTERVAL_MS);
    },
    [navigate],
  );

  useEffect(
    () => () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (abortRef.current) abortRef.current.abort();
      pollingEmpresaIdRef.current = null;
    },
    [],
  );

  /* --------------------------------------------------------
     Hook utilitário: re-sincroniza token do localStorage.
     O tokenRef já é inicializado SÍNCRONAMENTE acima para evitar
     o bug "Token obrigatório" no primeiro render. Este useEffect
     serve só como rede de segurança (caso o login troque o token
     enquanto a página está aberta).
  -------------------------------------------------------- */
  useEffect(() => {
    tokenRef.current = localStorage.getItem("token");
  }, []);

  // Espelha itemSelecionado em um ref para que `executarBusca` saiba
  // qual empresa está atualmente selecionada SEM depender do estado
  // (evita stale-closure e render loops).
  useEffect(() => {
    itemSelecionadoRef.current = itemSelecionado;
  }, [itemSelecionado]);

  const salvarCacheLista = useCallback((dados) => {
    salvarCacheComContexto(CACHE_KEY, obterIdentidadeSessao(), dados);
  }, []);

  /**
   * Seleciona um único item após uma busca que retornou apenas 1
   * resultado. Faz 1 request de status-completo e atualiza o stepper.
   * Não dispara polling aqui — o polling é iniciado em
   * `selecionarItem` após o usuário interagir (ou aqui mesmo quando
   * há auto-seleção).
   */
  const selecionarItemUnico = useCallback(
    async (item) => {
      if (!item) return;
      // Migração para o novo contrato: usa `tca_id` em primeiro lugar.
      const novoId = String(item.tca_id ?? item.tcaId ?? item.id);

      // Atualiza o card selecionado imediatamente (campos de identidade
      // já vêm da lista; o status virá em seguida via /status-completo).
      setItemSelecionado(item);
      localStorage.setItem("empresa_id_acompanhamento", novoId);
      localStorage.setItem(
        "empresa_busca_acompanhamento",
        montarDescricaoEmpresa(item),
      );

      // CORREÇÃO BUG-CLIQUE: a guarda de dedup anterior pulava o
      // /status-completo quando o polling já estava ativo, fazendo o
      // Stepper "andar" apenas no 2º clique (ou 15s depois do 1º).
      // Agora: se a etapa AINDA não foi resolvida (etapa_atual
      // null/1 sem fonte confiável), sempre busca o /status-completo;
      // caso já tenhamos a etapa resolvida E o polling esteja ativo
      // para o mesmo id, evitamos a chamada duplicada.
      const etapaJaResolvida = (() => {
        const etapa = item?.etapa_atual ?? item?.raw?.etapa_atual;
        return Number.isFinite(Number(etapa)) && Number(etapa) > 1;
      })();
      const puloDeDuplicacao =
        pollingEmpresaIdRef.current === novoId &&
        pollingRef.current &&
        etapaJaResolvida;
      if (puloDeDuplicacao) {
        return;
      }

      // Cancela requisição em voo (se houver).
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      try {
        // ⚠️ Wrapper em carregarAcompanhamentos.js espera
        // (empresaRef, token, signal) POSICIONAL.
        const contexto = await buscarStatusCompletoDaEmpresa(
          item,
          tokenRef.current,
          abortRef.current.signal,
        );

        setItemSelecionado((atual) => {
          // CORREÇÃO C: NÃO sobrescrever campos de identidade com
          // dados do `/status-completo`. Apenas campos DERIVADOS.
          const payloadCru = contexto.payload || {};

          // ANTI-REGRESSÃO: mesma proteção do polling — só aceitamos
          // uma etapa maior que a já conhecida; nunca regredir.
          const etapaJaVista = Number(
            atual?.etapa_atual ?? atual?.raw?.etapa_atual ?? 0,
          );
          const etapaNova = Number(payloadCru.etapa_atual ?? 0);
          const etapaFinal =
            Number.isFinite(etapaNova) && etapaNova > 0
              ? Math.max(etapaNova, etapaJaVista || 0)
              : etapaJaVista || null;

          const camposDerivados = {
            status: payloadCru.status ?? atual?.status,
            etapa_atual: etapaFinal ?? payloadCru.etapa_atual,
            total_etapas: payloadCru.total_etapas,
            label: payloadCru.label,
            cor: payloadCru.cor,
            status_label: payloadCru.status_label,
            rejeitado: payloadCru.rejeitado,
            finalizado: payloadCru.finalizado,
            supex: payloadCru.supex,
            ultimo_evento: payloadCru.ultimo_evento,
            historico: payloadCru.historico,
          };
          // camposDerivados aplicado por último para preservar a proteção anti-regressão (Math.max) em raw.etapa_atual
          const rawAtualizado = payloadCru
            ? { ...(atual?.raw || {}), ...payloadCru, ...camposDerivados }
            : atual?.raw;
          return {
            ...atual,
            ...camposDerivados,
            raw: rawAtualizado,
          };
        });

        // Inicia polling para manter o stepper atualizado.
        iniciarPolling(item);
      } catch (error) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          setErro("Sessão expirada. Faça login novamente.");
          navigate("/login", { replace: true });
          return;
        }
        setErro(`Erro ao atualizar status: ${error.message}`);
      }
    },
    [iniciarPolling, navigate],
  );

  const executarBusca = async () => {
    if (!tokenRef.current) {
      tokenRef.current = localStorage.getItem("token");
    }
    if (!tokenRef.current) {
      navigate("/login", { replace: true });
      return;
    }

    // Cancela busca anterior em voo (se houver).
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    try {
      setLoading(true);
      setErro("");

      const lista = await carregarAcompanhamentosDoUsuario(
        tokenRef.current,
        1,
        PAGE_SIZE,
        busca ? { busca } : {},
      );

      const dados = Array.isArray(lista) ? lista : [];
      setItens(dados);

      // Política de auto-seleção para alimentar o stepper sempre que
      // o usuário fizer uma busca com resultados:
      //  - qualquer quantidade > 0 → seleciona automaticamente o
      //    primeiro item e faz 1 request de status-completo
      //  - 0 resultados → limpa seleção e polling
      // O usuário pode clicar em outro card a qualquer momento para
      // trocar a seleção (1 request de status-completo).
      if (dados.length > 0) {
        // Auto-seleção do primeiro resultado: garante que o stepper
        // mostre os dados imediatamente após a busca.
        const primeiro = dados[0];
        // Migração para o novo contrato: usa `tca_id` em primeiro lugar.
        const idTcaPrimeiro = String(
          primeiro.tca_id ?? primeiro.tcaId ?? primeiro.id,
        );
        const idTcaAtual = itemSelecionadoRef.current
          ? String(
              itemSelecionadoRef.current.tca_id ??
                itemSelecionadoRef.current.tcaId ??
                itemSelecionadoRef.current.id,
            )
          : null;
        const mesmoItem = idTcaAtual && idTcaAtual === idTcaPrimeiro;

        if (mesmoItem) {
          // Mesma empresa já está selecionada: não refaz o request.
          return;
        }

        await selecionarItemUnico(primeiro);
        return;
      }

      if (dados.length === 0) {
        setItemSelecionado(null);
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = null;
        pollingEmpresaIdRef.current = null;
        return;
      }
    } catch (err) {
      setErro(
        err.response?.data?.message ||
          err.response?.data?.errors?.join(" | ") ||
          "Erro ao buscar acompanhamentos.",
      );
    } finally {
      setLoading(false);
    }
  };

  const selecionarItem = useCallback(
    async (item) => {
      if (!item) return;
      // Migração para o novo contrato: usa `tca_id` em primeiro lugar.
      const novoId = String(item.tca_id ?? item.tcaId ?? item.id);

      setItemSelecionado(item);
      localStorage.setItem("empresa_id_acompanhamento", novoId);
      localStorage.setItem(
        "empresa_busca_acompanhamento",
        montarDescricaoEmpresa(item),
      );

      // No caso do estudante, cada card do Acompanhamento representa
      // uma autorização individual (TCE ou TCA Empregador). Clicar no
      // card leva direto ao preenchimento daquela autorização
      // específica, passando o id como query param — assim as três
      // páginas (Preenchimento, Acompanhamento, Histórico) sempre
      // tratam a mesma autorização sem misturar dados.
      try {
        const decoded = jwtDecode(tokenRef.current || "");
        const isEstudante =
          decoded?.tipo === "estudante" ||
          Number(decoded?.acesso_autorizacao ?? 0) === 1;
        if (isEstudante) {
          const raw = item.raw || item || {};
          const tipoPrograma = Number(
            raw?.tipo_programa ?? raw?.tipoPrograma ?? decoded?.tipo_programa ?? 0,
          );
          const rota =
            tipoPrograma === 2 ? "/tca-empregador" : "/tce";
          navigate(`${rota}?id=${encodeURIComponent(item.id)}`, {
            replace: false,
          });
          return;
        }
      } catch (_) {
        /* cai no fluxo padrão */
      }

      // CORREÇÃO BUG-CLIQUE: mesma lógica do selecionarItemUnico.
      // Se etapa já foi resolvida E polling está ativo para o mesmo
      // id, evitamos chamada duplicada; senão sempre buscamos.
      const etapaJaResolvida = (() => {
        const etapa = item?.etapa_atual ?? item?.raw?.etapa_atual;
        return Number.isFinite(Number(etapa)) && Number(etapa) > 1;
      })();
      const puloDeDuplicacao =
        pollingEmpresaIdRef.current === novoId &&
        pollingRef.current &&
        etapaJaResolvida;
      if (puloDeDuplicacao) {
        return;
      }

      // Cancela requisição anterior em voo (se houver).
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      try {
        // ⚠️ Wrapper em carregarAcompanhamentos.js espera
        // (empresaRef, token, signal) POSICIONAL.
        const contexto = await buscarStatusCompletoDaEmpresa(
          item,
          tokenRef.current,
          abortRef.current.signal,
        );
        setItemSelecionado((atual) => {
          // CORREÇÃO C: NÃO sobrescrever campos de identidade com
          // dados do `/status-completo`. Apenas campos DERIVADOS.
          const payloadCru = contexto.payload || {};

          // ANTI-REGRESSÃO: mesma proteção do polling — só aceitamos
          // uma etapa maior que a já conhecida; nunca regredir.
          const etapaJaVista = Number(
            atual?.etapa_atual ?? atual?.raw?.etapa_atual ?? 0,
          );
          const etapaNova = Number(payloadCru.etapa_atual ?? 0);
          const etapaFinal =
            Number.isFinite(etapaNova) && etapaNova > 0
              ? Math.max(etapaNova, etapaJaVista || 0)
              : etapaJaVista || null;

          const camposDerivados = {
            status: payloadCru.status ?? atual?.status,
            etapa_atual: etapaFinal ?? payloadCru.etapa_atual,
            total_etapas: payloadCru.total_etapas,
            label: payloadCru.label,
            cor: payloadCru.cor,
            status_label: payloadCru.status_label,
            rejeitado: payloadCru.rejeitado,
            finalizado: payloadCru.finalizado,
            supex: payloadCru.supex,
            ultimo_evento: payloadCru.ultimo_evento,
            historico: payloadCru.historico,
          };
          // camposDerivados aplicado por último para preservar a proteção anti-regressão (Math.max) em raw.etapa_atual
          const rawAtualizado = payloadCru
            ? { ...(atual?.raw || {}), ...payloadCru, ...camposDerivados }
            : atual?.raw;
          return {
            ...atual,
            ...camposDerivados,
            raw: rawAtualizado,
          };
        });
        iniciarPolling(item);
      } catch (error) {
        // Ignora erro de cancelamento (esperado quando o usuário
        // troca de item rapidamente).
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }
        setErro(`Erro ao atualizar status: ${error.message}`);
      } finally {
        // Limpa a referência do controller se for o mesmo que criamos.
        if (abortRef.current?.signal?.aborted) {
          abortRef.current = null;
        }
      }
    },
    [iniciarPolling, navigate],
  );

  const carregarMais = async () => {
    if (!tokenRef.current || !temMais || carregandoMais) return;
    try {
      setCarregandoMais(true);
      const novos = await carregarAcompanhamentosDoUsuario(
        tokenRef.current,
        proximaPagina,
        PAGE_SIZE,
      );

      // Migração para o novo contrato: deduplica por `tca_id`.
      const idsExistentes = new Set(
        itens.map((i) => String(i.tca_id ?? i.tcaId ?? i.id)),
      );
      const filtrados = (Array.isArray(novos) ? novos : []).filter(
        (i) => !idsExistentes.has(String(i.tca_id ?? i.tcaId ?? i.id)),
      );

      const proximaLista = [...itens, ...filtrados];
      setItens(proximaLista);

      const aindaTemMais = filtrados.length === PAGE_SIZE;
      const proxima = aindaTemMais ? proximaPagina + 1 : proximaPagina;
      setTemMais(aindaTemMais);
      setProximaPagina(proxima);
      salvarCacheLista({
        lista: proximaLista,
        hasMore: aindaTemMais,
        nextPage: proxima,
      });
    } catch (error) {
      setErro(`Erro ao carregar mais acompanhamentos: ${error.message}`);
    } finally {
      setCarregandoMais(false);
    }
  };

  /* --------------------------------------------------------
     Derivados para render
  -------------------------------------------------------- */
  const listaFiltrada = itens;

  // tipoPrograma do usuário logado (lido do JWT). Define se o stepper
  // exibe a etapa extra "Aguardando Preenchimento do Aprendiz" e
  // "Contrato Finalizado" (programas 0 e 2) ou o fluxo padrão
  // (programa 1 / CIEE).
  const tipoProgramaLogado = obterTipoProgramaUsuarioLogado();

  const tipoProgramaSelecionado = useMemo(() => {
    if (!itemSelecionado) return tipoProgramaLogado;

    const raw = itemSelecionado.raw || itemSelecionado;

    const tipo =
      raw?.tipo_programa ??
      raw?.tipoPrograma ??
      itemSelecionado?.tipo_programa ??
      itemSelecionado?.tipoPrograma;

    const numero = Number(tipo);

    return Number.isFinite(numero) ? numero : tipoProgramaLogado;
  }, [itemSelecionado, tipoProgramaLogado]);

  const etapasExemplo = useMemo(
    () => obterEtapasExemplo(tipoProgramaSelecionado),
    [tipoProgramaSelecionado],
  );

  const statusItem = useMemo(() => {
    if (!itemSelecionado) return null;
    const raw = itemSelecionado.raw || itemSelecionado;
    const listaExcecoes = [];
    const requerSupex = cadastroRequerSupex(
      raw,
      raw,
      listaExcecoes,
      itemSelecionado,
    );
    const etapaRejeicaoHint = obterEtapaRejeicaoPorHint(
      raw,
      raw,
      itemSelecionado,
      requerSupex,
      tipoProgramaSelecionado,
    );

    return (
      normalizarStatusCadastro(
        itemSelecionado,
        raw,
        requerSupex,
        etapaRejeicaoHint,
        tipoProgramaSelecionado,
      ) || {
        statusLabel:
          STATUS_LABELS[(itemSelecionado.status || "").toUpperCase()] ||
          itemSelecionado.status ||
          "—",
        statusCor:
          STATUS_CORES[(itemSelecionado.status || "").toUpperCase()] ||
          "#94a3b8",
        etapaAtual:
          STATUS_PARA_ETAPA[(itemSelecionado.status || "").toUpperCase()] || 1,
        totalEtapas: obterTotalEtapasPorPrograma(
          tipoProgramaSelecionado,
          requerSupex,
        ),
      }
    );
  }, [itemSelecionado, tipoProgramaSelecionado]);

  const etapaAtual = statusItem?.etapaAtual ?? 1;

  /* --------------------------------------------------------
     Helpers para resolver CNPJ/CNPJ-empresa a partir de um item
     (vem normalizado pelo mapper ou direto do backend).
  -------------------------------------------------------- */
  const resolverCnpjItem = (item) => {
    if (!item || typeof item !== "object") return "";
    return (
      item.cnpj ||
      item.cnpj_ou_cpf ||
      item.cnpj_empresa ||
      item?.raw?.cnpj ||
      item?.raw?.cnpj_ou_cpf ||
      item?.raw?.cnpj_empresa ||
      item?.raw?.empresa?.cnpj ||
      item?.raw?.cadastro?.cnpj ||
      item?.raw?.estudante?.cnpj ||
      item?.documento ||
      ""
    );
  };

  const resolverRazaoSocial = (item) => {
    if (!item || typeof item !== "object") return "";
    return (
      item.razao_social ||
      item.razaoSocial ||
      item.razaosocial ||
      item?.raw?.razao_social ||
      item?.raw?.razaoSocial ||
      item?.raw?.razaosocial ||
      item?.raw?.empresa?.razao_social ||
      item?.raw?.empresa?.razaoSocial ||
      item?.raw?.cadastro?.razao_social ||
      item?.raw?.estudante?.razao_social ||
      item?.nome ||
      ""
    );
  };

  const resolverConvenio = (item) => {
    if (!item || typeof item !== "object") return "";
    return (
      item.convenio ||
      item.numero_convenio ||
      item.numeroConvenio ||
      item?.raw?.convenio ||
      item?.raw?.numero_convenio ||
      item?.raw?.numeroConvenio ||
      item?.raw?.empresa?.convenio ||
      item?.raw?.empresa?.numero_convenio ||
      ""
    );
  };

  /* --------------------------------------------------------
     Render
  -------------------------------------------------------- */
  return (
    <div className="acompanhamento-container">
      <header className="acompanhamento-header">
        <div className="acompanhamento-header-titulo">
          <h1>Acompanhamento – CIEEMG</h1>
          <p>Acompanhe o status atual e as próximas etapas do processo.</p>
        </div>
        <button
          className="acompanhamento-btn-voltar"
          onClick={() => {
            // Para o estudante, voltar do Acompanhamento deve levar
            // para a tela de Preenchimento (TCE / TCA Emp.).
            try {
              const tk = localStorage.getItem("token");
              if (tk) {
                const dec = jwtDecode(tk);
                if (
                  dec?.tipo === "estudante" ||
                  Number(dec?.acesso_autorizacao ?? 0) === 1
                ) {
                  const tp = Number(dec?.tipo_programa ?? 0);
                  navigate(tp === 2 ? "/tca-empregador" : "/tce", {
                    replace: false,
                  });
                  return;
                }
              }
            } catch (_) {
              /* fallback */
            }
            navigate("/dashboard");
          }}
        >
          <FaArrowLeft /> Voltar
        </button>
      </header>

      <div className="acompanhamento-filtros">
        {(() => {
          // Para o estudante, a lista é individual (uma entrada por
          // autorização vinculada ao CPF dele). O campo de busca por
          // CNPJ/Razão social não se aplica — escondemos para não dar a
          // impressão de que um filtro genérico funciona aqui.
          let isEstudanteAqui = false;
          try {
            const tk = localStorage.getItem("token");
            if (tk) {
              const dec = jwtDecode(tk);
              isEstudanteAqui =
                dec?.tipo === "estudante" ||
                Number(dec?.acesso_autorizacao ?? 0) === 1;
            }
          } catch (_) {
            isEstudanteAqui = false;
          }
          if (isEstudanteAqui) return null;
          return (
            <>
              <div className="acompanhamento-input-wrapper">
                <FaSearch className="acompanhamento-input-icone" size={14} />
                <input
                  type="text"
                  placeholder="Buscar por CNPJ, Razão social, Nome do Estudante, CPF, Número do convênio, Código da empresa"
                  value={buscaDigitada}
                  onChange={(e) => setBuscaDigitada(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && executarBusca()}
                  className="acompanhamento-input-busca"
                />
              </div>
              <BotaoBusca onClick={executarBusca} />
            </>
          );
        })()}
      </div>

      <section className="acompanhamento-stepper-card">
        <div className="identificacao-bar">
          <div className="identificacao-item">
            <span className="identificacao-label">CNPJ:</span>
            <span className="identificacao-valor">
              {itemSelecionado
                ? (() => {
                    const cnpjResolvido = resolverCnpjItem(itemSelecionado);
                    const docCnpj = formatarCnpjCpf(cnpjResolvido);
                    const programa = resolverIdentificacaoPrograma(
                      itemSelecionado.raw || itemSelecionado,
                      cnpjResolvido,
                    );
                    const corPrograma = "#721c24";
                    const fundoPrograma = "#f8d7da";
                    const bordaPrograma = "#721c24";
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span>{docCnpj || "—"}</span>
                        {programa ? (
                          <span
                            style={{
                              display: "inline-block",
                              padding: "2px 10px",
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 600,
                              color: corPrograma,
                              backgroundColor: fundoPrograma,
                              border: `1px solid ${bordaPrograma}`,
                              letterSpacing: 0.2,
                            }}
                          >
                            {programa.rotulo}
                          </span>
                        ) : null}
                      </span>
                    );
                  })()
                : "—"}
            </span>
          </div>
          <div className="identificacao-item">
            <span className="identificacao-label">Aprendiz:</span>
            <span className="identificacao-valor">
              {itemSelecionado
                ? itemSelecionado.nome_completo ||
                  itemSelecionado.nome ||
                  itemSelecionado.nome_estudante ||
                  "—"
                : "Selecione um registro abaixo"}
            </span>
          </div>
          {statusItem && (
            <div className="identificacao-item">
              <span
                className="status-badge"
                style={{
                  backgroundColor: statusItem.statusCor + "22",
                  color: statusItem.statusCor,
                }}
              >
                {statusItem.statusLabel}
              </span>
            </div>
          )}
        </div>

        <Stepper etapaAtual={etapaAtual} etapas={etapasExemplo} />
      </section>

      {erro && (
        <p className="acompanhamento-vazio" style={{ color: "#dc2626" }}>
          {erro}
        </p>
      )}

      {loading && <Loader message="Buscando autorizações..." />}

      {!loading && listaFiltrada.length === 0 && (
        <p className="acompanhamento-vazio">
          {busca
            ? "Nenhum registro encontrado para o filtro informado."
            : "Nenhum registro encontrado."}
        </p>
      )}

      {!loading && listaFiltrada.length > 0 && (
        <>
          <div className="acompanhamento-lista">
            {listaFiltrada.map((item) => {
              const tipoProgramaItem = (() => {
                const raw = item?.raw || item;
                const tipo =
                  item?.tipo_programa ??
                  item?.tipoPrograma ??
                  raw?.tipo_programa ??
                  raw?.tipoPrograma;
                const numero = Number(tipo);
                return Number.isFinite(numero)
                  ? numero
                  : tipoProgramaLogado;
              })();
              const resumo = obterResumoStatusItem(item, tipoProgramaItem);
              const id = item.tca_id ?? item.tcaId ?? item.id;
              const nome =
                item.nome_completo ||
                item.nome ||
                item.nome_estudante ||
                item.razao_social ||
                `Registro #${id}`;
              const isSelecionado = itemSelecionado?.id === id;

              return (
                <div
                  className="acompanhamento-card"
                  key={id}
                  onClick={() => selecionarItem(item)}
                  style={
                    isSelecionado
                      ? { borderColor: "#134b8b", borderWidth: 2 }
                      : undefined
                  }
                >
                  <div className="acompanhamento-card-info">
                    <h3>{nome}</h3>
                    {(() => {
                      const razao = resolverRazaoSocial(item);
                      if (!razao || razao === nome) return null;
                      return (
                        <p
                          style={{
                            margin: "0.15rem 0 0",
                            fontSize: 12,
                            color: "#475569",
                            fontWeight: 500,
                          }}
                        >
                          {razao}
                        </p>
                      );
                    })()}
                    {(() => {
                      const cnpjCard = resolverCnpjItem(item);
                      if (!cnpjCard) return null;
                      return (
                        <p
                          style={{
                            margin: "0.25rem 0 0",
                            fontSize: 12,
                            color: "#64748b",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            flexWrap: "wrap",
                          }}
                        >
                          <span>CNPJ/CPF: {formatarCnpjCpf(cnpjCard)}</span>
                          {(() => {
                            const programa = resolverIdentificacaoPrograma(
                              item.raw || item,
                              resolverCnpjItem(item),
                            );
                            if (!programa) return null;
                            const corPrograma = "#721c24";
                            const fundoPrograma = "#f8d7da";
                            const bordaPrograma = "#721c24";
                            return (
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "1px 8px",
                                  borderRadius: 999,
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: corPrograma,
                                  backgroundColor: fundoPrograma,
                                  border: `1px solid ${bordaPrograma}`,
                                  letterSpacing: 0.2,
                                  marginLeft: 4,
                                }}
                              >
                                {programa.rotulo}
                              </span>
                            );
                          })()}
                        </p>
                      );
                    })()}
                    <p
                      style={{
                        margin: "0.25rem 0 0",
                        fontSize: 12,
                        color: "#64748b",
                      }}
                    >
                      Etapa {resumo.etapaAtual}/{resumo.totalEtapas} · Progresso{" "}
                      {resumo.progresso}%
                    </p>
                  </div>
                  <span
                    className="status-badge"
                    style={{
                      backgroundColor: resumo.statusCor + "22",
                      color: resumo.statusCor,
                    }}
                  >
                    {resumo.statusLabel}
                  </span>
                </div>
              );
            })}
          </div>
          {temMais && (
            <div className="acompanhamento-carregar-mais-wrapper">
              <button
                id="acompanhamento-carregar-mais"
                className="btn"
                onClick={carregarMais}
                disabled={carregandoMais}
                style={{ margin: "12px" }}
              >
                {carregandoMais ? "Carregando..." : "Carregar mais"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Acompanhamento;
